import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { FiSearch, FiChevronDown, FiPhone, FiMapPin, FiDollarSign, FiX, FiCreditCard, FiLoader, FiBookOpen } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { colors, spacing, borderRadius, typography } from '../theme/colors';
import { formatAmount } from '../utils/documentNumbering';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { getLinkedAccount } from '../utils/coaInitializer';
import { focusNext } from '../utils/focusNext';
import { getRegionsCached, getAreasCached } from '../utils/lookupCache';
import { getVendorsCached } from '../utils/masterDataStore';

const CustomerSelect = ({ customers, selectedCustomerId, onChange, disabled, placeholder = '-- Select Customer --', allOption = null, balanceOnDemand = false }) => {
  const { activeCompany, activeBranch, activeFinancialYear, isModuleEnabled } = useAuth();
  const navigate = useNavigate();
  const unifiedMode = isModuleEnabled && isModuleEnabled('unified_party_picker');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customerBalance, setCustomerBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [regionsMap, setRegionsMap] = useState({});
  const [areasMap, setAreasMap] = useState({});
  const [extraVendors, setExtraVendors] = useState([]);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Unified mode: also load vendors and merge them into the picker
  useEffect(() => {
    if (!unifiedMode || !activeCompany?.id) { setExtraVendors([]); return; }
    (async () => {
      try {
        // Tier 1/6 (Firebase cost): device-cached vendors instead of a full read
        // on every unified-picker mount.
        const all = await getVendorsCached(activeCompany.id);
        const vendors = all
          .filter(v => v.isActive !== false)
          .map(v => ({
            ...v,
            // Map to customer-shape fields for display compatibility
            companyName: v.vendorName || v.companyName || '',
            _partyType: 'vendor'
          }));
        setExtraVendors(vendors);
      } catch (err) { console.error('Error loading vendors for unified picker:', err); }
    })();
  }, [unifiedMode, activeCompany?.id]);

  // Tag native customers with _partyType so list rendering can show the badge
  const allParties = useMemo(() => {
    const tagged = (customers || []).map(c => ({ ...c, _partyType: c._partyType || 'customer' }));
    if (!unifiedMode) return tagged;
    return [...tagged, ...extraVendors];
  }, [customers, extraVendors, unifiedMode]);

  // Load regions and areas for display
  useEffect(() => {
    if (!activeCompany?.id) return;
    const loadRegionsAreas = async () => {
      try {
        // Tier 6 (Firebase cost): display-only name lookups that used to be a
        // fresh getDocs on EVERY CustomerSelect mount (every sales form). Cached.
        const [regRows, areaRows] = await Promise.all([
          getRegionsCached(activeCompany.id),
          getAreasCached(activeCompany.id)
        ]);
        const rMap = {};
        regRows.forEach(d => { rMap[d.id] = d.regionName || ''; });
        setRegionsMap(rMap);
        const aMap = {};
        areaRows.forEach(d => { aMap[d.id] = d.areaName || ''; });
        setAreasMap(aMap);
      } catch (err) { console.error('Error loading regions/areas:', err); }
    };
    loadRegionsAreas();
  }, [activeCompany]);

  const selectedCustomer = useMemo(() =>
    allParties.find(c => c.id === selectedCustomerId) || null,
    [allParties, selectedCustomerId]
  );

  // Calculate customer balance from subTransactions
  const fetchCustomerBalance = useCallback(async (customer) => {
    if (!activeCompany?.id) {
      setCustomerBalance(customer?.openingBalance || 0);
      return;
    }

    setLoadingBalance(true);
    try {
      const subTransRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      let totalDebits = 0;
      let totalCredits = 0;
      let foundEntries = false;
      let linkedAccountId = customer?.linkedAccountId;

      // Helper function to process entries - include both Posted AND Draft (for opening balances)
      const branchFilter = activeBranch && activeBranch.id !== 'all' ? activeBranch.id : null;
      // ACTIVE FINANCIAL YEAR ONLY. Opening balances are entered per year, and the
      // new year's opening balance already contains last year's closing — so adding
      // both years together showed exactly double (1,000 became 2,000). This now
      // matches what the Ledger report shows. Rows with no year are still counted.
      const fyFilter = activeFinancialYear?.id || null;
      const processEntries = (snapshot) => {
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          // Filter by branch if active
          if (branchFilter && data.branchId !== branchFilter) return;
          if (fyFilter && data.financialYearId && data.financialYearId !== fyFilter) return;
          // Include Posted entries always, and Draft entries only from OB (Opening Balance) transactions
          if (data.status === 'Posted' || (data.status === 'Draft' && data.tranTypeCode === 'OB')) {
            totalDebits += data.debitAmount || 0;
            totalCredits += data.creditAmount || 0;
          }
        });
        return !snapshot.empty;
      };

      // Strategy 0: If customer doesn't have linkedAccountId, look up the linked account by customerId
      if (!linkedAccountId && customer?.id) {
        try {
          const linkedAccount = await getLinkedAccount(activeCompany.id, 'customer', customer.id);
          if (linkedAccount?.id) {
            linkedAccountId = linkedAccount.id;
          }
        } catch (err) {
          console.log('Could not find linked account for customer:', err);
        }
      }

      // Strategy 1: Query by linkedAccountId (document ID)
      if (linkedAccountId) {
        const q1 = query(subTransRef, where('accountId', '==', linkedAccountId));
        const snapshot1 = await getDocs(q1);
        if (processEntries(snapshot1)) {
          foundEntries = true;
        }
      }

      // Strategy 2: Query by accountCode if linkedAccountCode exists
      if (!foundEntries && customer?.linkedAccountCode) {
        const q2 = query(subTransRef, where('accountCode', '==', customer.linkedAccountCode));
        const snapshot2 = await getDocs(q2);
        if (processEntries(snapshot2)) {
          foundEntries = true;
        }
      }

      // Strategy 3: Query by accountName containing customer name
      if (!foundEntries && customer?.companyName) {
        const q3 = query(subTransRef, where('accountName', '==', `Customer: ${customer.companyName}`));
        const snapshot3 = await getDocs(q3);
        if (processEntries(snapshot3)) {
          foundEntries = true;
        }
      }

      // Strategy 4: Query by partyId (customer document ID)
      if (!foundEntries && customer?.id) {
        const q4 = query(subTransRef, where('partyId', '==', customer.id));
        const snapshot4 = await getDocs(q4);
        if (processEntries(snapshot4)) {
          foundEntries = true;
        }
      }

      // If still no entries found, fallback to openingBalance from customer master
      if (!foundEntries) {
        setCustomerBalance(customer?.openingBalance || 0);
        return;
      }

      // For customer (receivable account): Debits increase balance (what they owe us), Credits decrease (payments)
      // Balance = Debits - Credits (positive means they owe us)
      const balance = totalDebits - totalCredits;
      setCustomerBalance(balance);
    } catch (error) {
      console.error('Error fetching customer balance:', error);
      // Fallback to opening balance on error
      setCustomerBalance(customer?.openingBalance || 0);
    } finally {
      setLoadingBalance(false);
    }
  }, [activeCompany, activeBranch]);

  // Fetch balance when selected customer changes (skip in filter mode).
  // Firebase cost: when `balanceOnDemand`, we DON'T auto-scan the ledger on every
  // customer pick — the balance is fetched only when the user clicks "View
  // Balance". Most invoices never need it, so this removes a per-selection ledger
  // read. Reset to null on change so the button reappears for the new customer.
  useEffect(() => {
    if (allOption) return;
    setCustomerBalance(null);
    if (!balanceOnDemand && selectedCustomer) {
      fetchCustomerBalance(selectedCustomer);
    }
  }, [selectedCustomer, fetchCustomerBalance, allOption, balanceOnDemand]);

  const getRegionArea = useCallback((customer) => {
    const region = customer?.regionId ? regionsMap[customer.regionId] : '';
    const area = customer?.areaId ? areasMap[customer.areaId] : '';
    return { region, area };
  }, [regionsMap, areasMap]);

  const filteredCustomers = useMemo(() => {
    if (!search.trim()) return allParties;
    const q = search.toLowerCase();
    return allParties.filter(c => {
      const { region, area } = getRegionArea(c);
      return c.companyName?.toLowerCase().includes(q) ||
        c.contactPerson?.toLowerCase().includes(q) ||
        c.phone?.includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        region?.toLowerCase().includes(q) ||
        area?.toLowerCase().includes(q);
    });
  }, [allParties, search, getRegionArea]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (customer, { advanceFocus = false } = {}) => {
    // Pass full party object as 2nd arg so forms can read _partyType + linkedAccountId
    // (backward-compatible: forms ignoring the 2nd arg still work)
    onChange(customer.id, customer);
    setIsOpen(false);
    setSearch('');
    setHighlightedIndex(0);
    if (advanceFocus) {
      setTimeout(() => {
        if (triggerRef.current) focusNext(triggerRef.current);
      }, 30);
    } else {
      setTimeout(() => triggerRef.current?.focus(), 30);
    }
  };

  const isAllSelected = allOption ? selectedCustomerId === allOption.value : !selectedCustomerId;

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(allOption ? allOption.value : '', null);
    setSearch('');
  };

  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
    setHighlightedIndex(0);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // Reset highlighted index when filter changes
  useEffect(() => { setHighlightedIndex(0); }, [search]);

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    // When closed & a customer is already selected, Enter advances to next field
    if (!isOpen && selectedCustomer && e.key === 'Enter') {
      e.preventDefault();
      focusNext(triggerRef.current);
      return;
    }
    if (!isOpen && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      handleOpen();
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(i => Math.min(i + 1, filteredCustomers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const c = filteredCustomers[highlightedIndex];
      if (c) handleSelect(c, { advanceFocus: true });
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setSearch('');
      setTimeout(() => triggerRef.current?.focus(), 30);
    } else if (e.key === 'Tab') {
      setIsOpen(false);
      setSearch('');
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <SelectTrigger
        ref={triggerRef}
        onClick={handleOpen}
        onKeyDown={handleTriggerKeyDown}
        tabIndex={disabled ? -1 : 0}
        $disabled={disabled}
        $hasValue={!!selectedCustomer}
      >
        {isOpen ? (
          <SearchInputWrapper>
            <FiSearch size={14} />
            <SearchInput
              ref={inputRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search customer by name, contact, phone, email..."
              onClick={e => e.stopPropagation()}
            />
          </SearchInputWrapper>
        ) : (
          <TriggerText $hasValue={!!selectedCustomer}>
            {selectedCustomer ? (
              <>
                {selectedCustomer.companyName}
                {(() => {
                  const { region, area } = getRegionArea(selectedCustomer);
                  const parts = [region, area].filter(Boolean);
                  return parts.length > 0 ? <TriggerSubText> — {parts.join(', ')}</TriggerSubText> : null;
                })()}
              </>
            ) : (allOption ? allOption.label : placeholder)}
          </TriggerText>
        )}
        <TriggerIcons>
          {selectedCustomer && !isAllSelected && !disabled && <ClearBtn onClick={handleClear}><FiX size={14} /></ClearBtn>}
          <FiChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
        </TriggerIcons>
      </SelectTrigger>

      {isOpen && (
        <Dropdown>
          {filteredCustomers.length === 0 ? (
            <NoResults>No customers found</NoResults>
          ) : (
            filteredCustomers.map((c, idx) => {
              const { region, area } = getRegionArea(c);
              const locationParts = [region, area].filter(Boolean);
              const isVendor = c._partyType === 'vendor';
              return (
                <DropdownItem
                  key={`${c._partyType}_${c.id}`}
                  onClick={() => handleSelect(c, { advanceFocus: true })}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  $selected={c.id === selectedCustomerId}
                  $highlighted={idx === highlightedIndex}
                >
                  <DropdownItemLeft>
                    <CustomerName>
                      {unifiedMode && (
                        <PartyBadge $vendor={isVendor}>{isVendor ? 'V' : 'C'}</PartyBadge>
                      )}
                      {c.companyName}
                    </CustomerName>
                    {locationParts.length > 0 && (
                      <CustomerLocation><FiMapPin size={10} /> {locationParts.join(', ')}</CustomerLocation>
                    )}
                  </DropdownItemLeft>
                  <DropdownItemRight>
                    {c.contactPerson && <CustomerContact>{c.contactPerson}</CustomerContact>}
                    {c.phone && <CustomerPhone>{c.phone}</CustomerPhone>}
                  </DropdownItemRight>
                </DropdownItem>
              );
            })
          )}
        </Dropdown>
      )}

      {selectedCustomer && !allOption && (
        <CustomerInfoPanel>
          <InfoRow>
            <InfoChip><FiMapPin size={12} /> {selectedCustomer.address ? `${selectedCustomer.address}${selectedCustomer.city ? ', ' + selectedCustomer.city : ''}` : 'No address'}</InfoChip>
            <InfoChip><FiPhone size={12} /> {selectedCustomer.phone || 'No phone'}</InfoChip>
            <InfoChip $highlight $positive={customerBalance > 0}>
              <FiDollarSign size={12} />
              {loadingBalance ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <FiLoader size={10} style={{ animation: 'spin 1s linear infinite' }} /> Loading...
                </span>
              ) : (balanceOnDemand && customerBalance === null) ? (
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={(e) => { e.stopPropagation(); if (selectedCustomer) fetchCustomerBalance(selectedCustomer); }}
                  title="Click to calculate this customer's balance (reads the ledger)"
                >View Balance</span>
              ) : (
                <>Balance: {formatAmount(customerBalance || 0)}</>
              )}
            </InfoChip>
            {selectedCustomer.creditLimit > 0 && (
              <InfoChip>
                <FiCreditCard size={12} /> Credit Limit: {formatAmount(selectedCustomer.creditLimit)}
              </InfoChip>
            )}
            {selectedCustomer.linkedAccountId && (
              <LedgerLinkChip
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/reports/ledger', { state: { filterAccountId: selectedCustomer.linkedAccountId } });
                }}
                title="Open this customer's ledger"
              >
                <FiBookOpen size={12} /> View Ledger
              </LedgerLinkChip>
            )}
          </InfoRow>
        </CustomerInfoPanel>
      )}
    </Wrapper>
  );
};

// ===== STYLED =====
const Wrapper = styled.div`position:relative;`;
const SelectTrigger = styled.div`
  display:flex;align-items:center;justify-content:space-between;
  padding:${spacing.sm} ${spacing.md};
  border:1px solid ${props => props.$hasValue ? colors.primary.main + '60' : colors.border.light};
  border-radius:${borderRadius.sm};font-size:${typography.fontSize.sm};
  color:${colors.text.primary};background:${props => props.$disabled ? colors.neutral[100] : colors.background.default};
  cursor:${props => props.$disabled ? 'not-allowed' : 'pointer'};
  min-height:36px;transition:border-color 0.2s, box-shadow 0.2s;
  &:hover{border-color:${props => props.$disabled ? colors.border.light : colors.primary.main};}
  &:focus{outline:none;border-color:${colors.primary.main};box-shadow:0 0 0 2px ${colors.primary.main}30;}
`;
const TriggerText = styled.span`
  color:${props => props.$hasValue ? colors.text.primary : colors.text.tertiary};
  display:flex;align-items:baseline;gap:0;flex:1;min-width:0;overflow:hidden;
`;
const TriggerSubText = styled.span`
  font-size:11px;color:${colors.text.tertiary};font-weight:400;white-space:nowrap;
`;
const TriggerIcons = styled.div`display:flex;align-items:center;gap:4px;color:${colors.text.secondary};`;
const ClearBtn = styled.button`
  display:flex;align-items:center;justify-content:center;
  width:20px;height:20px;background:${colors.neutral[200]};border:none;
  border-radius:50%;cursor:pointer;color:${colors.text.secondary};
  &:hover{background:${colors.error.main};color:white;}
`;
const SearchInputWrapper = styled.div`display:flex;align-items:center;gap:${spacing.sm};flex:1;color:${colors.text.secondary};`;
const SearchInput = styled.input`
  border:none;outline:none;background:none;flex:1;
  font-size:${typography.fontSize.sm};color:${colors.text.primary};
  &::placeholder{color:${colors.text.tertiary};}
`;
const Dropdown = styled.div`
  position:absolute;top:100%;left:0;right:0;z-index:100;
  max-height:240px;overflow-y:auto;
  background:${colors.background.default};
  border:1px solid ${colors.border.light};border-top:none;
  border-radius:0 0 ${borderRadius.sm} ${borderRadius.sm};
  box-shadow:${colors.shadows?.md || '0 3px 6px rgba(0,0,0,0.15)'};
`;
const DropdownItem = styled.div`
  display:flex;align-items:center;justify-content:space-between;gap:${spacing.sm};
  padding:8px ${spacing.md};cursor:pointer;
  background:${props => props.$highlighted ? colors.primary.main + '15' : props.$selected ? colors.primary.main + '10' : 'transparent'};
  border-left:${props => props.$selected ? `3px solid ${colors.primary.main}` : '3px solid transparent'};
  &:hover{background:${colors.neutral[50]};}
  &:not(:last-child){border-bottom:1px solid ${colors.neutral[100]};}
`;
const DropdownItemLeft = styled.div`
  display:flex;flex-direction:column;gap:1px;flex:1;min-width:0;
`;
const DropdownItemRight = styled.div`
  display:flex;flex-direction:column;align-items:flex-end;gap:1px;flex-shrink:0;
`;
const CustomerName = styled.span`font-size:${typography.fontSize.sm};color:${colors.text.primary};font-weight:${typography.fontWeight.medium};display:inline-flex;align-items:center;gap:6px;`;
const PartyBadge = styled.span`
  display:inline-block;width:18px;height:18px;
  background:${props => props.$vendor ? colors.warning.main + '25' : colors.info.main + '25'};
  color:${props => props.$vendor ? colors.warning.dark : colors.info.dark};
  border-radius:4px;font-size:10px;font-weight:700;
  text-align:center;line-height:18px;
  flex-shrink:0;
`;
const CustomerLocation = styled.span`
  display:inline-flex;align-items:center;gap:3px;
  font-size:11px;color:${colors.text.tertiary};font-weight:${typography.fontWeight.regular};
`;
const CustomerContact = styled.span`font-size:${typography.fontSize.xs};color:${colors.text.secondary};`;
const CustomerPhone = styled.span`font-size:${typography.fontSize.xs};color:${colors.text.tertiary};`;
const NoResults = styled.div`padding:${spacing.md};text-align:center;color:${colors.text.tertiary};font-size:${typography.fontSize.sm};`;

const CustomerInfoPanel = styled.div`
  margin-top:6px;padding:8px ${spacing.md};
  background:${colors.neutral[50]};border:1px solid ${colors.border.light};
  border-radius:${borderRadius.sm};
`;
const InfoRow = styled.div`display:flex;flex-wrap:wrap;gap:${spacing.sm};align-items:center;`;
const LedgerLinkChip = styled.button`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.primary.main};
  background: ${colors.primary.main}10;
  border: 1px solid ${colors.primary.main}30;
  padding: 2px 10px;
  border-radius: 12px;
  cursor: pointer;
  &:hover { background: ${colors.primary.main}; color: white; }
`;

const InfoChip = styled.span`
  display:inline-flex;align-items:center;gap:4px;
  font-size:${typography.fontSize.xs};
  color:${props => props.$positive ? colors.success.main : props.$highlight ? colors.primary.main : colors.text.secondary};
  font-weight:${props => props.$highlight ? typography.fontWeight.semibold : typography.fontWeight.regular};
  padding:2px 8px;background:${colors.background.default};border:1px solid ${colors.border.light};
  border-radius:12px;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export default CustomerSelect;
